import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { AdminDataSyncService } from '@services/users/admin-data-sync.service';
import { AdminOrganizationListEntryDto } from 'app/@types/dto/admin-organization-list-entry-dto.model';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { ConfigService } from 'app/app-config.service';
import { Observable, Subject, combineLatest, takeUntil } from 'rxjs';
import { FeatureService } from '@services/features/feature.service';
import { LoaderService } from '@services/loader/loader.service';

interface OrganizationListEntry {
    uid: string;
    name: string;
    index: number;
    solutionCount: number;
    selected: boolean;
}

@Component({
    selector: 'app-dedicated-admin-organisation-list',
    templateUrl: './dedicated-admin-organisation-list.component.html',
    styleUrls: ['./dedicated-admin-organisation-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DedicatedAdminOrganisationListComponent implements OnInit, OnDestroy {
    // when main admin instanciate this component, it needs an id. if it is a dedicatedAdmin it does not

    private ngUnsubscribe: Subject<void> = new Subject();
    public isLoaded: boolean = false;
    public isGlobalAdmin: boolean = false;
    clientName = this.configService.getClient.name;

    constructor(
        private configService: ConfigService,
        private AdminHttpService: AdminHTTPService,
        private appRouting: AppRoutingService,
        public adminSignals: AdminDataSyncService,
        private featureService: FeatureService,
        private loader: LoaderService,
    ) {}

    ngOnInit(): void {
        this.getData();
        this.isGlobalAdmin =
            this.featureService.hasUiFeatures(['admin_admin'], false) ||
            this.featureService.hasUiFeatures(['admin_admin'], true);
        this.adminSignals.udpateListSubject.pipe(takeUntil(this.ngUnsubscribe)).subscribe((bool) => {
            if (bool) {
                this.getData();
            }
        });
        this.adminSignals.currentSelectedOrgId.pipe(takeUntil(this.ngUnsubscribe)).subscribe((orgId) => {
            this.organizations.forEach((o) => {
                o.selected = this.adminSignals.currentSelectedOrgId.getValue() === o.uid;
            });
        });
        this.adminSignals.currentSelectedDedicatedAdminId
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.getData());
    }

    getData() {
        this.adminSignals.loading.next(true);
        this.isLoaded = false;
        combineLatest([
            this.AdminHttpService.getOrganizationList(this.adminSignals.currentSelectedDedicatedAdminId.getValue()),
            this.AdminHttpService.getUnassignedOrganizationList(),
        ])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: ([organizationList, unassignedOrganizationsList]) => {
                    this.organizations = this.mapData(organizationList);
                    this.organizations.sort((org1, org2) => {
                        return org1.name.localeCompare(org2.name);
                    });
                    this.unassignedOrganizations = this.mapData(unassignedOrganizationsList);
                    this.unassignedOrganizations.sort((org1, org2) => {
                        return org1.name.localeCompare(org2.name);
                    });
                    this.isLoaded = true;
                    this.adminSignals.loading.next(false);
                },
                error: () => {
                    this.organizations = [];
                    this.unassignedOrganizations = [];
                    this.isLoaded = true;
                    this.adminSignals.loading.next(false);
                },
            });
    }

    mapData(data: AdminOrganizationListEntryDto[]): OrganizationListEntry[] {
        return data.map((p, i) => {
            const currentSelectedOrgId = this.adminSignals.currentSelectedOrgId.getValue();
            return {
                ...p,
                name: p.name,
                uid: p.uid ?? '',
                index: i,
                selected: currentSelectedOrgId === p.uid,
            };
        });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public organizations: OrganizationListEntry[] = [];
    public unassignedOrganizations: OrganizationListEntry[] = [];

    getRandomColor(index: number): string {
        const colors = this.configService.getClient.colors;
        return colors[index % colors.length];
    }

    routeToOrganisationForm(organization: OrganizationListEntry): void {
        this.adminSignals.currentSelectedOrgId.next(organization.uid);
        this.organizations.forEach((o) => {
            o.selected = this.adminSignals.currentSelectedOrgId.getValue() === o.uid;
        });

        this.appRouting.navigateToOganisationForm(organization.uid);
    }

    routeToNewOrganisationForm(): void {
        this.adminSignals.udpateListSubject.next(true);
        this.adminSignals.currentSelectedOrgId.next(undefined);
        this.appRouting.navigateToOganisationForm();
    }
}
